import { useEffect, useState } from "react";

import {
  deleteProject,
  fetchProjectHealth,
  fetchProjects,
  saveProject,
} from "./api";
import type { FleetNode, Project, ProjectDraft } from "./domain";
import { clusterProjectMapPoints, deriveProjectHealth } from "./operations";
import { NodeStatusLabel } from "./Visualizations";
import { tokens } from "./tokens";

type ProjectState =
  | { readonly status: "loading" }
  | { readonly status: "ready"; readonly projects: readonly Project[] }
  | { readonly status: "error"; readonly message: string };

type SaveState = "idle" | "saving" | "error";

const emptyProject = (): ProjectDraft => ({
  name: "",
  customerName: "",
  contactName: "",
  email: "",
  phone: "",
  location: "",
  address: "",
  latitude: null,
  longitude: null,
  notes: "",
  nodeIds: [],
  mapPolicy: "hidden",
  preserveCoordinates: false,
});

type HealthWindow = "24h" | "7d" | "30d";
type ProjectHealthState = { readonly status: "loading" } | {
  readonly status: "error";
} | {
  readonly status: "ready";
  readonly values: Readonly<
    Record<
      HealthWindow,
      { readonly samples: number; readonly availabilityPercent: number | null }
    >
  >;
};

function ProjectHealthSummary(
  { projectId, nodes, now }: {
    readonly projectId: string;
    readonly nodes: readonly FleetNode[];
    readonly now: Date;
  },
) {
  const [health, setHealth] = useState<ProjectHealthState>({
    status: "loading",
  });
  useEffect(() => {
    let active = true;
    void Promise.all(
      (["24h", "7d", "30d"] as const).map(async (window) =>
        [window, await fetchProjectHealth(projectId, window)] as const
      ),
    )
      .then((entries) => {
        if (active) {
          setHealth({
            status: "ready",
            values: Object.fromEntries(entries) as Record<
              HealthWindow,
              {
                readonly samples: number;
                readonly availabilityPercent: number | null;
              }
            >,
          });
        }
      })
      .catch(() => {
        if (active) setHealth({ status: "error" });
      });
    return () => {
      active = false;
    };
  }, [projectId]);
  const current = deriveProjectHealth(nodes, now, []);
  const lastContact = nodes.length
    ? Math.max(...nodes.map((node) => Date.parse(node.lastSeenAt)))
    : null;
  if (health.status === "loading") {
    return (
      <div>
        <p>
          {current.total} nodes · {current.worst} · {current.attention}{" "}
          need attention
        </p>
        <p role="status">Loading availability…</p>
      </div>
    );
  }
  if (health.status === "error") {
    return (
      <div>
        <p>
          {current.total} nodes · {current.worst} · last contact{" "}
          {lastContact === null
            ? "Unavailable"
            : new Date(lastContact).toLocaleString()}
        </p>
        <p>Availability evidence is unavailable.</p>
      </div>
    );
  }
  return (
    <div className="availability" aria-label="Project availability">
      <p>
        {current.total} nodes · worst state {current.worst} ·{" "}
        {current.attention} need attention · last contact {lastContact === null
          ? "Unavailable"
          : new Date(lastContact).toLocaleString()}
      </p>
      <div className="availability__bars" aria-hidden="true">
        {(["24h", "7d", "30d"] as const).map((window) => (
          <span
            key={window}
            style={{
              "--availability": `${
                health.values[window].availabilityPercent ?? 0
              }%`,
            } as React.CSSProperties}
          />
        ))}
      </div>
      <table>
        <caption>Availability by reporting window</caption>
        <thead>
          <tr>
            <th>Window</th>
            <th>Available</th>
            <th>Samples</th>
          </tr>
        </thead>
        <tbody>
          {(["24h", "7d", "30d"] as const).map((window) => (
            <tr key={window}>
              <td>{window}</td>
              <td>
                {health.values[window].availabilityPercent === null
                  ? "Unavailable"
                  : `${health.values[window].availabilityPercent}%`}
              </td>
              <td>{health.values[window].samples}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ProjectMap({ projects }: { readonly projects: readonly Project[] }) {
  const clusters = clusterProjectMapPoints(projects);
  if (clusters.length === 0) {
    return (
      <div className="empty">
        <h3>No project coordinates are visible</h3>
        <p>
          Choose approximate or exact coordinate visibility on a project to
          include it in this private map.
        </p>
      </div>
    );
  }
  const projectCount = clusters.reduce(
    (total, cluster) => total + cluster.names.length,
    0,
  );
  return (
    <figure className="project-map">
      <figcaption>
        <h3>Private project map</h3>
        <p>
          Rendered locally from policy-filtered coordinates. Nearby projects are
          clustered; no tile provider receives them.
        </p>
      </figcaption>
      <svg
        viewBox="0 0 360 180"
        role="img"
        aria-labelledby="project-map-title project-map-description"
      >
        <title id="project-map-title">Project locations</title>
        <desc id="project-map-description">
          A world-coordinate plot of {projectCount} visible projects in{" "}
          {clusters.length}{" "}
          location groups. The project list below is the authoritative text
          alternative.
        </desc>
        <path
          className="project-map__grid"
          d="M0 45H360M0 90H360M0 135H360M90 0V180M180 0V180M270 0V180"
        />
        {clusters.map((cluster) => (
          <g
            key={cluster.id}
            transform={`translate(${cluster.longitude + 180},${
              90 - cluster.latitude
            })`}
          >
            <circle
              r={cluster.names.length > 1
                ? tokens.map.clusterRadius
                : tokens.map.markerRadius}
            />
            <text x="9" y="3">
              {cluster.names.length > 1
                ? `${cluster.names.length} projects: ${
                  cluster.names.join(", ")
                }`
                : cluster.names[0]}
            </text>
          </g>
        ))}
      </svg>
    </figure>
  );
}

/** Render encrypted customer projects and multi-node assignment controls. */
export function Projects(
  { nodes, csrf, now }: {
    readonly nodes: readonly FleetNode[];
    readonly csrf: string;
    readonly now: Date;
  },
) {
  const [state, setState] = useState<ProjectState>({ status: "loading" });
  const [draft, setDraft] = useState<ProjectDraft>(emptyProject);
  const [editingId, setEditingId] = useState<string | undefined>();
  const [saveState, setSaveState] = useState<SaveState>("idle");
  const [locationState, setLocationState] = useState<
    "idle" | "reading" | "denied"
  >("idle");
  const [mapVisible, setMapVisible] = useState(false);
  const [deleteState, setDeleteState] = useState<
    { readonly status: "idle" } | {
      readonly status: "confirm" | "deleting" | "error";
      readonly projectId: string;
    }
  >({ status: "idle" });

  const load = async (): Promise<void> => {
    try {
      setState({ status: "ready", projects: await fetchProjects() });
    } catch {
      setState({ status: "error", message: "Projects could not be loaded." });
    }
  };
  useEffect(() => {
    void load();
  }, []);

  const update = <K extends keyof ProjectDraft>(
    key: K,
    value: ProjectDraft[K],
  ): void => setDraft((current) => ({ ...current, [key]: value }));
  const edit = (project: Project): void => {
    setEditingId(project.id);
    setDraft({
      name: project.name,
      customerName: project.customerName,
      contactName: project.contactName,
      email: project.email,
      phone: project.phone,
      location: project.location,
      address: project.address,
      latitude: project.latitude,
      longitude: project.longitude,
      notes: project.notes,
      nodeIds: project.nodeIds,
      mapPolicy: project.mapPolicy ?? "hidden",
      preserveCoordinates: true,
    });
    setSaveState("idle");
  };
  const reset = (): void => {
    setEditingId(undefined);
    setDraft(emptyProject());
    setSaveState("idle");
  };
  const toggleNode = (nodeId: string): void =>
    update(
      "nodeIds",
      draft.nodeIds.includes(nodeId)
        ? draft.nodeIds.filter((id) => id !== nodeId)
        : [...draft.nodeIds, nodeId],
    );
  const locate = (): void => {
    if (!navigator.geolocation) {
      setLocationState("denied");
      return;
    }
    setLocationState("reading");
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setDraft((current) => ({
          ...current,
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          preserveCoordinates: false,
        }));
        setLocationState("idle");
      },
      () => setLocationState("denied"),
      { enableHighAccuracy: false, timeout: 10_000, maximumAge: 300_000 },
    );
  };
  const submit = async (
    event: React.FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    event.preventDefault();
    if (saveState === "saving") return;
    setSaveState("saving");
    try {
      await saveProject(draft, csrf, editingId);
      reset();
      await load();
    } catch {
      setSaveState("error");
    }
  };
  const remove = async (projectId: string): Promise<void> => {
    if (deleteState.status === "deleting") return;
    setDeleteState({ status: "deleting", projectId });
    try {
      await deleteProject(projectId, csrf);
      setDeleteState({ status: "idle" });
      if (editingId === projectId) reset();
      await load();
    } catch {
      setDeleteState({ status: "error", projectId });
    }
  };

  const projects = state.status === "ready" ? state.projects : [];
  return (
    <section className="projects" aria-labelledby="projects-title">
      <div className="section-heading">
        <div>
          <h2 id="projects-title">Projects</h2>
          <p>Customer context and node assignments</p>
        </div>
        <div className="project-card__actions">
          <button
            type="button"
            onClick={() => setMapVisible((visible) => !visible)}
          >
            {mapVisible ? "Hide private map" : "Show private map"}
          </button>
          <button type="button" onClick={reset}>New project</button>
        </div>
      </div>
      {mapVisible ? <ProjectMap projects={projects} /> : null}
      <div className="project-layout">
        <div className="project-list" aria-live="polite">
          {state.status === "loading"
            ? <p role="status">Loading projects…</p>
            : null}
          {state.status === "error"
            ? (
              <div className="empty">
                <p role="alert">{state.message}</p>
                <button type="button" onClick={() => void load()}>
                  Try again
                </button>
              </div>
            )
            : null}
          {state.status === "ready" && projects.length === 0
            ? (
              <div className="empty">
                <h3>No projects yet</h3>
                <p>
                  Create a project to group nodes with the customer and location
                  they serve.
                </p>
              </div>
            )
            : null}
          {projects.map((project) => {
            const assignedNodes = project.nodeIds.flatMap((nodeId) => {
              const node = nodes.find((candidate) => candidate.id === nodeId);
              return node ? [node] : [];
            });
            return (
              <article
                className="project-card"
                id={`project-${project.id}`}
                key={project.id}
              >
                <div className="project-card__heading">
                  <div>
                    <p className="eyebrow">
                      {project.customerName || "Internal"}
                    </p>
                    <h3>{project.name}</h3>
                  </div>
                  <div className="project-card__actions">
                    <button
                      className="text-button"
                      type="button"
                      onClick={() => edit(project)}
                    >
                      Edit
                    </button>
                    {deleteState.status === "confirm" &&
                        deleteState.projectId === project.id
                      ? (
                        <>
                          <button
                            className="danger-button"
                            type="button"
                            onClick={() => void remove(project.id)}
                          >
                            Confirm delete
                          </button>
                          <button
                            className="text-button"
                            type="button"
                            onClick={() => setDeleteState({ status: "idle" })}
                          >
                            Cancel
                          </button>
                        </>
                      )
                      : (
                        <button
                          className="text-button"
                          type="button"
                          onClick={() =>
                            setDeleteState({
                              status: "confirm",
                              projectId: project.id,
                            })}
                        >
                          Delete
                        </button>
                      )}
                  </div>
                </div>
                {deleteState.status === "error" &&
                    deleteState.projectId === project.id
                  ? (
                    <p role="alert" className="error">
                      The project could not be deleted. Try again.
                    </p>
                  )
                  : null}
                <p>
                  {[project.location, project.address].filter(Boolean).join(
                    " · ",
                  ) || "No location recorded"}
                </p>
                {project.latitude !== null && project.longitude !== null
                  ? (
                    <a
                      className="map-link"
                      href={`https://www.openstreetmap.org/?mlat=${
                        encodeURIComponent(project.latitude)
                      }&mlon=${encodeURIComponent(project.longitude)}#map=16/${
                        encodeURIComponent(project.latitude)
                      }/${encodeURIComponent(project.longitude)}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Open coordinates in OpenStreetMap
                    </a>
                  )
                  : null}
                <ProjectHealthSummary
                  projectId={project.id}
                  nodes={assignedNodes}
                  now={now}
                />
                <ul className="project-nodes">
                  {project.nodeIds.length === 0
                    ? <li>No nodes assigned</li>
                    : project.nodeIds.map((nodeId) => {
                      const node = nodes.find((candidate) =>
                        candidate.id === nodeId
                      );
                      return (
                        <li key={nodeId}>
                          {node
                            ? (
                              <>
                                <span>{node.name}</span>
                                <NodeStatusLabel node={node} now={now} />
                              </>
                            )
                            : <span>Unavailable node</span>}
                        </li>
                      );
                    })}
                </ul>
              </article>
            );
          })}
        </div>
        <form className="project-form" onSubmit={(event) => void submit(event)}>
          <p className="eyebrow">
            {editingId ? "Edit project" : "Create project"}
          </p>
          <h3>
            {editingId ? draft.name || "Untitled project" : "Project details"}
          </h3>
          <label>
            Project name<input
              required
              maxLength={80}
              value={draft.name}
              onChange={(event) => update("name", event.target.value)}
            />
          </label>
          <label>
            Customer name<input
              maxLength={120}
              value={draft.customerName}
              onChange={(event) => update("customerName", event.target.value)}
            />
          </label>
          <div className="form-pair">
            <label>
              Contact name<input
                maxLength={120}
                value={draft.contactName}
                onChange={(event) => update("contactName", event.target.value)}
              />
            </label>
            <label>
              Email<input
                type="email"
                maxLength={254}
                value={draft.email}
                onChange={(event) => update("email", event.target.value)}
              />
            </label>
          </div>
          <label>
            Phone<input
              maxLength={32}
              value={draft.phone}
              onChange={(event) => update("phone", event.target.value)}
            />
          </label>
          <div className="form-pair">
            <label>
              Location<input
                maxLength={120}
                placeholder="City or site name"
                value={draft.location}
                onChange={(event) => update("location", event.target.value)}
              />
            </label>
            <label>
              Address<textarea
                maxLength={300}
                rows={3}
                value={draft.address}
                onChange={(event) => update("address", event.target.value)}
              />
            </label>
          </div>
          <fieldset>
            <legend>Map geolocation</legend>
            <label>
              Coordinate visibility<select
                value={draft.mapPolicy ?? "hidden"}
                onChange={(event) =>
                  update(
                    "mapPolicy",
                    event.target.value as ProjectDraft["mapPolicy"],
                  )}
              >
                <option value="hidden">Hidden</option>
                <option value="approximate">Approximate</option>
                <option value="exact">Exact</option>
              </select>
            </label>
            {draft.preserveCoordinates
              ? (
                <p>
                  Stored coordinates are protected. Enter both fields only to
                  replace them.
                </p>
              )
              : null}
            <div className="coordinate-row">
              <label>
                Latitude<input
                  type="number"
                  min={-90}
                  max={90}
                  step="any"
                  value={draft.latitude ?? ""}
                  onChange={(event) =>
                    setDraft((current) => ({
                      ...current,
                      latitude: event.target.value === ""
                        ? null
                        : Number(event.target.value),
                      preserveCoordinates: false,
                    }))}
                />
              </label>
              <label>
                Longitude<input
                  type="number"
                  min={-180}
                  max={180}
                  step="any"
                  value={draft.longitude ?? ""}
                  onChange={(event) =>
                    setDraft((current) => ({
                      ...current,
                      longitude: event.target.value === ""
                        ? null
                        : Number(event.target.value),
                      preserveCoordinates: false,
                    }))}
                />
              </label>
            </div>
            <button
              type="button"
              onClick={locate}
              disabled={locationState === "reading"}
            >
              {locationState === "reading"
                ? "Reading location…"
                : "Use this device’s location"}
            </button>
            {locationState === "denied"
              ? (
                <p role="alert" className="error">
                  Location was unavailable. Enter coordinates manually.
                </p>
              )
              : null}
          </fieldset>
          <fieldset>
            <legend>Assigned nodes</legend>
            <div className="node-checks">
              {nodes.length === 0
                ? <p>No active nodes are available.</p>
                : nodes.map((node) => (
                  <label key={node.id}>
                    <input
                      type="checkbox"
                      checked={draft.nodeIds.includes(node.id)}
                      onChange={() => toggleNode(node.id)}
                    />
                    <span>{node.name}</span>
                  </label>
                ))}
            </div>
          </fieldset>
          <label>
            Notes<textarea
              maxLength={1_000}
              rows={4}
              value={draft.notes}
              onChange={(event) => update("notes", event.target.value)}
            />
          </label>
          {saveState === "error"
            ? (
              <p role="alert" className="error">
                The project could not be saved. Check the fields and try again.
              </p>
            )
            : null}
          <div className="form-actions">
            <button type="submit" disabled={saveState === "saving"}>
              {saveState === "saving"
                ? "Saving…"
                : editingId
                ? "Save changes"
                : "Create project"}
            </button>
            {editingId
              ? <button type="button" onClick={reset}>Cancel</button>
              : null}
          </div>
        </form>
      </div>
    </section>
  );
}
